import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { authHeader } from "@/lib/token";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowUpCircle, FileText, Loader2, Server, Stethoscope } from "lucide-react";

const timeAgo = (iso) => {
  if (!iso) return null;
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `il y a ${hours} h`;
  return `il y a ${Math.round(hours / 24)} jours`;
};

const STATE_STYLE = {
  ok: "border-status-success/40 text-status-success",
  waiting: "border-status-warning/40 text-status-warning",
  offline: "border-status-warning/40 text-status-warning",
  error: "border-status-error/40 text-status-error",
};

/** Dernier contact du serveur avec la licence + bouton « Tester ma licence ». */
export function LicenseStatus({ bot }) {
  const [result, setResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const test = async () => {
    setTesting(true);
    try {
      const r = await axios.post(`${API}/bots/${bot.id}/license/check`, {}, { headers: authHeader() });
      setResult(r.data);
    } catch (e) {
      setResult({ state: "error", message: e.response?.data?.detail || "Test impossible pour le moment" });
    } finally {
      setTesting(false);
    }
  };
  const lastSeen = result?.last_seen_at ?? bot.license_last_seen_at;
  const device = result?.device_hint ?? bot.license_device_hint;
  return (
    <div className="space-y-1.5" data-testid={`license-status-${bot.id}`}>
      {(lastSeen || device) && (
        <div className="flex items-center justify-between text-xs text-text-muted">
          <span className="flex items-center gap-1"><Server className="w-3 h-3" />{device ? `Serveur ${device}` : "Serveur"}</span>
          <span>{lastSeen ? `Vu ${timeAgo(lastSeen)}` : "Jamais vu"}</span>
        </div>
      )}
      <button data-testid={`test-license-${bot.id}`} onClick={test} disabled={testing} className="w-full flex items-center justify-center gap-1.5 text-xs text-text-secondary hover:text-white border border-white/10 rounded-lg py-1.5 disabled:opacity-50">
        {testing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Stethoscope className="w-3 h-3" />}
        Tester ma licence
      </button>
      {result && <p data-testid={`license-result-${bot.id}`} className={`text-xs border rounded-lg p-2 ${STATE_STYLE[result.state] || STATE_STYLE.error}`}>{result.message}</p>}
    </div>
  );
}

/** « Passer au pack » sur un produit possédé à vie qui fait partie d'un pack. */
export function UpgradeToBundleButton({ bot, products }) {
  const navigate = useNavigate();
  if (bot.plan_id !== "lifetime" || bot.is_trial) return null;
  const bundle = products.find((p) => p.includes?.includes(bot.bot_type));
  if (!bundle) return null;
  return (
    <Button data-testid={`upgrade-bot-${bot.id}`} onClick={() => navigate(`/checkout?plan=lifetime&bot=${bundle.id}&upgrade=1`)} className="w-full bg-blue-600/15 hover:bg-blue-600/25 text-blue-100 rounded-lg text-sm border border-blue-500/30">
      <ArrowUpCircle className="w-4 h-4 mr-2" />
      Passer au {bundle.name.replace(/^VantaPhone - /, "")} (payer la différence)
    </Button>
  );
}

/** Arrêter le renouvellement automatique (résiliation en ligne). */
export function CancelRenewalButton({ bot, onDone }) {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  if (!bot.auto_renew) return null;
  const confirm = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/bots/${bot.id}/subscription/cancel`, {}, { headers: authHeader() });
      toast.success("Renouvellement arrêté : le produit reste actif jusqu'à la fin de la période payée");
      onDone && onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible d'arrêter le renouvellement");
    } finally {
      setSending(false);
      setOpen(false);
    }
  };
  return (
    <>
      <button data-testid={`cancel-renewal-${bot.id}`} onClick={() => setOpen(true)} className="text-xs text-white/40 hover:text-white underline decoration-dotted text-left">
        Arrêter le renouvellement automatique
      </button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent className="bg-vanta-surface border-border-default">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Arrêter le renouvellement ?</AlertDialogTitle>
            <AlertDialogDescription className="text-text-secondary">
              Vous ne serez plus prélevé pour <span className="text-white">{bot.bot_name}</span>. Le produit reste actif jusqu'à la fin de la période déjà payée. Un e-mail vous confirme la résiliation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border-default hover:bg-vanta-base rounded-none">Garder l'abonnement</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-cancel-renewal" onClick={confirm} disabled={sending} className="bg-status-error hover:bg-status-error/80 rounded-none">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Arrêter le renouvellement"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/** Factures (ou reçus) des paiements. */
export function InvoicesSection() {
  const [invoices, setInvoices] = useState(null);
  const [downloading, setDownloading] = useState(null);
  useEffect(() => {
    axios.get(`${API}/invoices`, { headers: authHeader() }).then((r) => setInvoices(r.data || [])).catch(() => setInvoices([]));
  }, []);
  const download = async (invoice) => {
    setDownloading(invoice.id);
    try {
      const r = await axios.get(`${API}/invoices/${invoice.id}/pdf`, { headers: authHeader(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `facture-${invoice.number || invoice.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Téléchargement impossible");
    } finally {
      setDownloading(null);
    }
  };
  if (!invoices?.length) return null;
  return (
    <div data-testid="invoices-section" className="mb-8">
      <h2 className="font-heading text-xl font-semibold mb-6">Factures</h2>
      <div className="bg-vanta-surface border border-border-default divide-y divide-border-default">
        {invoices.map((invoice) => (
          <div key={invoice.id} className="p-4 flex flex-wrap items-center gap-4">
            <FileText className="w-5 h-5 text-text-muted shrink-0" />
            <div className="flex-1 min-w-[180px]">
              <p className="text-sm font-medium">{invoice.product} — {invoice.plan}{invoice.renewal ? " (renouvellement)" : ""}</p>
              <p className="text-xs text-text-muted">{invoice.number} · {new Date(invoice.date).toLocaleDateString("fr-FR")}</p>
            </div>
            <span className="font-semibold">{invoice.amount.toFixed(2).replace(".", ",")} €</span>
            <Button size="sm" variant="outline" onClick={() => download(invoice)} disabled={downloading === invoice.id} className="rounded-none border-border-default text-xs">
              {downloading === invoice.id ? <Loader2 className="w-3 h-3 animate-spin" /> : "PDF"}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
